import { Component, OnInit, inject } from '@angular/core';
import { ModalController, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent, IonGrid, IonRow, IonCol } from '@ionic/angular/standalone';
import { Observable } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { DbService } from '../../services/db.service';
import { Payee } from '../../../models/external-system.model';
import { PayeeService } from '../../services/payee.service';
import { AsyncPipe } from '@angular/common';
import { ArtistCardComponent } from '../artist-card/artist-card.component';

@Component({
  selector: 'app-linked-payees-list',
  templateUrl: './linked-payees-list.component.html',
  styleUrls: ['./linked-payees-list.component.scss'],
  imports: [
    ArtistCardComponent,
    AsyncPipe,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonGrid,
    IonRow,
    IonCol,
  ],
  standalone: true,
})
export class LinkedPayeesListComponent implements OnInit {
  modalController = inject(ModalController);
  private payeeService = inject(PayeeService);
  private db = inject(DbService);

  signedInPayee$: Observable<Payee>;
  linkedPayees$: Observable<Payee[]>;

  ngOnInit() {
    this.signedInPayee$ = this.payeeService.signedInPayee$;
    
    this.linkedPayees$ = this.signedInPayee$.pipe(
      switchMap(payee => {
        const parentPayeeId = payee?.internalProperties?.parentPayee;
        
        // Get the parent payee (either fetch it or use current payee if it's the parent)
        const parentPayee$ = parentPayeeId 
          ? this.db.getPayee(parentPayeeId)
          : new Observable<Payee>(subscriber => {
              subscriber.next(payee);
              subscriber.complete();
            });
        
        return parentPayee$.pipe(
          switchMap(parentPayee => {
            const linkedPayeeIds = parentPayee?.internalProperties?.linkedPayees || [];
            
            if (!linkedPayeeIds || linkedPayeeIds.length === 0) {
              return new Observable<Payee[]>(subscriber => {
                subscriber.next(parentPayee ? [parentPayee] : []);
                subscriber.complete();
              });
            }
            
            return this.db.getPayees(linkedPayeeIds).pipe(
              map(linkedPayees => {
                const sortedLinkedPayees = this.sortedPayees(linkedPayees);
                return parentPayee ? [parentPayee, ...sortedLinkedPayees] : sortedLinkedPayees;
              })
            );
          })
        );
      })
    );
  }

  dismiss(payee?: Payee) {
    this.modalController.dismiss(payee?.id);
  }

  sortedPayees(payees?: Payee[] | null): Payee[] {
    if (!payees || payees.length === 0) {
      return [];
    }
    // Do not mutate the original array; create a shallow copy before sorting
    return [...payees].sort((a, b) => {
      const aName = (a?.companyName || '').toLocaleLowerCase();
      const bName = (b?.companyName || '').toLocaleLowerCase();
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    });
  }
}

